import React, { useState, useRef } from 'react';
import { 
  X, 
  Upload as UploadIcon, 
  FileText, 
  Download, 
  ChevronRight, 
  Check,
  AlertCircle,
  Loader2,
  Users,
  Truck
} from 'lucide-react';
import { cn } from '../lib/utils';
import { getDefaultCurrency } from '../lib/currency';
import { collection, addDoc, serverTimestamp } from '../firebase';
import { db, auth, handleFirestoreError, OperationType } from '../firebase';
import { notify } from '../services/notify';

const getLocalAuthToken = () => localStorage.getItem('app_auth_token');
const isLocalSession = () => Boolean(getLocalAuthToken());

const localApiRequest = async <T,>(path: string, init: RequestInit = {}): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: {
      Authorization: `Bearer ${getLocalAuthToken()}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok) throw new Error(payload?.error || `Request failed (${response.status})`);
  return payload as T;
};

interface ContactImportProps {
  onClose: () => void;
  onComplete: () => void;
  orgId: string;
  orgSettings?: any;
}

export default function ContactImport({ onClose, onComplete, orgId, orgSettings }: ContactImportProps) {
  const organizationCurrency = getDefaultCurrency(orgSettings);
  const [step, setStep] = useState(1);
  const [importType, setImportType] = useState<'Customer' | 'Supplier' | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [mappedData, setMappedData] = useState<any[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (!file || !importType) return;
    setIsUploading(true);
    // Simulate parsing
    setTimeout(() => {
      setIsUploading(false);
      // Dummy data for preview
      setMappedData([
        { name: importType === 'Customer' ? 'Acme Corp' : 'Global Supplies', email: 'contact@example.com', type: importType, currency: organizationCurrency },
        { name: importType === 'Customer' ? 'Stark Industries' : 'Tech Parts Ltd', email: 'info@stark.com', type: importType, currency: organizationCurrency },
      ]);
      setStep(3);
    }, 1500);
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'Name', 'Email', 'Phone', 'Type', 'Address', 
      'Website', 'Tax Number', 'Account Number', 'Currency', 
      'Payment Terms', 'Notes'
    ];
    const csvContent = [
      headers.join(','),
      `${importType === 'Customer' ? 'Acme Corp' : 'Global Supplies'},contact@example.com,+123456789,${importType},123 Main St,www.example.com,TAX123,ACC456,${organizationCurrency},Net 30,Initial import`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${importType?.toLowerCase()}_import_template.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFinalImport = async () => {
    setIsImporting(true);
    try {
      if (isLocalSession()) {
        for (const contact of mappedData) {
          await localApiRequest(`/api/organizations/${orgId}/contacts`, {
            method: 'POST',
            body: JSON.stringify({
              ...contact,
              type: importType,
              status: 'Active',
              emails: contact.email ? [contact.email] : [],
              phones: contact.phone ? [{ number: contact.phone, countryCode: '+1' }] : [],
              contactPersons: [],
            })
          });
        }
      } else {
        for (const contact of mappedData) {
          await addDoc(collection(db, 'contacts'), {
            ...contact,
            orgId,
            status: 'Active',
            emails: [contact.email],
            phones: contact.phone ? [{ number: contact.phone, countryCode: '+1' }] : [],
            contactPersons: [],
            createdBy: auth.currentUser?.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      }
      notify.confirm(`Successfully imported ${mappedData.length} ${importType}s`);
      onComplete();
    } catch (error) {
      console.error("Import error:", error);
      notify.error(error instanceof Error ? error.message : "Failed to import contacts");
    } finally {
      setIsImporting(false);
    }
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-4 mb-12">
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          <div className="flex items-center gap-2">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all",
              step === s ? "bg-[#141414] text-[#E4E3E0]" : 
              step > s ? "bg-green-500 text-white" : "bg-[#141414]/10 text-[#141414]/40"
            )}>
              {step > s ? <Check className="w-4 h-4" /> : s}
            </div>
            <span className={cn(
              "text-sm font-bold",
              step === s ? "opacity-100" : "opacity-40"
            )}>
              {s === 1 ? 'Type' : s === 2 ? 'Upload' : 'Review'}
            </span>
          </div>
          {s < 3 && <div className="w-12 h-[2px] bg-[#141414]/10" />}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div
      className="fixed inset-0 overflow-y-auto overscroll-contain bg-[#141414]/40 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="bg-[#E4E3E0] border border-[#141414] w-full max-w-4xl rounded-[40px] p-12 shadow-2xl relative overflow-hidden flex flex-col max-h-[calc(100dvh-2rem)]">
        <button 
          onClick={onClose}
          className="absolute top-8 right-8 p-2 hover:bg-[#141414] hover:text-[#E4E3E0] rounded-full transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {renderStepIndicator()}

        <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
          {step === 1 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="text-center">
                <h2 className="text-4xl font-bold tracking-tight mb-4">What would you like to import?</h2>
                <p className="text-lg opacity-60 italic serif leading-relaxed max-w-2xl mx-auto">
                  Select the type of contacts you want to bring into the system.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button 
                  onClick={() => {
                    setImportType('Customer');
                    setStep(2);
                  }}
                  className="bg-white border border-[#141414] p-8 rounded-[32px] hover:shadow-xl transition-all group text-left"
                >
                  <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Users className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Import Customers</h3>
                  <p className="text-sm opacity-50 italic serif">Import your client database and sales contacts</p>
                </button>

                <button 
                  onClick={() => {
                    setImportType('Supplier');
                    setStep(2);
                  }}
                  className="bg-white border border-[#141414] p-8 rounded-[32px] hover:shadow-xl transition-all group text-left"
                >
                  <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <Truck className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">Import Suppliers</h3>
                  <p className="text-sm opacity-50 italic serif">Import your vendors and service providers</p>
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h2 className="text-4xl font-bold tracking-tight mb-4">Upload {importType}s</h2>
                <p className="text-lg opacity-60 italic serif leading-relaxed max-w-2xl">
                  Upload a CSV file containing your {importType?.toLowerCase()} records.
                </p>
              </div>

              <div className="flex justify-center">
                <button 
                  onClick={handleDownloadTemplate}
                  className="flex items-center gap-2 border border-[#141414] px-6 py-3 rounded-full hover:bg-[#141414] hover:text-[#E4E3E0] transition-all font-bold"
                >
                  <Download className="w-4 h-4" />
                  <span>Download {importType} Template</span>
                </button>
              </div>

              <div className="space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-widest opacity-40">File to upload</h3>
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "border-2 border-dashed border-[#141414]/20 rounded-3xl p-12 text-center cursor-pointer hover:border-[#141414] hover:bg-white/20 transition-all group relative",
                    file && "border-green-500 bg-green-50/50"
                  )}
                >
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    className="hidden" 
                    accept=".csv"
                    onChange={handleFileSelect}
                  />
                  {!file ? (
                    <>
                      <div className="w-16 h-16 bg-[#141414]/5 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <UploadIcon className="w-8 h-8 opacity-40" />
                      </div>
                      <p className="text-xl font-bold italic serif mb-2">Drag and drop CSV file or select manually</p>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                        className="bg-[#141414] text-[#E4E3E0] px-8 py-3 rounded-full font-bold mt-4 hover:opacity-90 transition-opacity relative z-10"
                      >
                        Select file
                      </button>
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-4">
                      <div className="p-4 bg-green-100 text-green-600 rounded-2xl">
                        <FileText className="w-8 h-8" />
                      </div>
                      <div className="text-left">
                        <p className="text-lg font-bold">{file.name}</p>
                        <p className="text-sm opacity-50">{(file.size / 1024).toFixed(2)} KB</p>
                      </div>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          setFile(null);
                        }}
                        className="ml-4 p-2 hover:bg-red-100 text-red-500 rounded-full transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <button 
                  onClick={() => setStep(1)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
                >
                  Back
                </button>
                <button 
                  disabled={!file || isUploading}
                  onClick={handleUpload}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity disabled:opacity-30 flex items-center gap-2"
                >
                  {isUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Continue'}
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
              <div>
                <h2 className="text-4xl font-bold tracking-tight mb-4">Review {importType}s</h2>
                <p className="text-lg opacity-60 italic serif leading-relaxed">
                  We found {mappedData.length} {importType?.toLowerCase()}s in your file.
                </p>
              </div>

              <div className="bg-white/30 border border-[#141414] rounded-3xl overflow-hidden overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#141414] text-[#E4E3E0] uppercase text-[10px] tracking-widest font-bold sticky top-0 z-10">
                      <th className="px-6 py-4">Name</th>
                      <th className="px-6 py-4">Email</th>
                      <th className="px-6 py-4">Type</th>
                      <th className="px-6 py-4 text-right">Currency</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#141414]/10">
                    {mappedData.map((contact, i) => (
                      <tr key={i} className="hover:bg-white/20 transition-colors">
                        <td className="px-6 py-4 text-sm font-bold">{contact.name}</td>
                        <td className="px-6 py-4 text-sm">{contact.email}</td>
                        <td className="px-6 py-4 text-sm">{contact.type}</td>
                        <td className="px-6 py-4 text-right text-sm font-bold font-mono">
                          {contact.currency}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-between pt-4">
                <button 
                  onClick={() => setStep(2)}
                  className="px-8 py-4 rounded-full font-bold hover:bg-[#141414]/5 transition-all"
                >
                  Back
                </button>
                <button 
                  disabled={isImporting}
                  onClick={handleFinalImport}
                  className="bg-[#141414] text-[#E4E3E0] px-12 py-4 rounded-full font-bold hover:opacity-90 transition-opacity flex items-center gap-2"
                >
                  {isImporting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Complete Import'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
